/**
 * Month Availability Service
 * 
 * Centralized service for month selection validation based on business creation date.
 * Ensures consistent validation across all month selection components and prevents
 * navigation to periods before business creation.
 */

import { getMonth, getYear } from 'date-fns';

// Type definitions for business data inputs
export type BusinessCreationInput = 
  | string                    // ISO date string from database
  | Date                     // Parsed Date object
  | null 
  | undefined;

export interface BusinessDetails {
  created_at?: string;
}

export interface MonthAvailabilityResult {
  isBeforeBusinessCreation: (month: number, year: number) => boolean;
  isYearNavigationDisabled: (targetYear: number, direction: 'previous' | 'next') => boolean;
  isMonthNavigationDisabled: (currentMonth: number, currentYear: number, direction: 'previous' | 'next') => boolean;
  businessCreationYear: number | null;
  businessCreationMonth: number | null;
  businessCreationDate: Date | null;
}

/**
 * Parse business creation date from various input formats
 */
function parseBusinessCreationDate(input: BusinessCreationInput | BusinessDetails): Date | null {
  if (!input) return null;
  
  // Handle BusinessDetails object
  if (typeof input === 'object' && 'created_at' in input) {
    return input.created_at ? new Date(input.created_at) : null;
  }
  
  // Handle Date object
  if (input instanceof Date) {
    return input;
  }
  
  // Handle ISO string
  if (typeof input === 'string') {
    return new Date(input);
  }
  
  return null;
}

/**
 * Core month availability service
 * 
 * @param businessData - Business creation date in various formats
 * @returns Object with validation functions and business creation info
 */
export function useMonthAvailability(
  businessData: BusinessCreationInput | BusinessDetails
): MonthAvailabilityResult {
  const businessCreationDate = parseBusinessCreationDate(businessData);
  const businessCreationYear = businessCreationDate ? getYear(businessCreationDate) : null;
  const businessCreationMonth = businessCreationDate ? getMonth(businessCreationDate) : null;

  /**
   * Check if a month/year combination is before business creation
   * @param month - Month index (0-based: 0 = January)
   * @param year - Full year (e.g., 2025)
   */
  const isBeforeBusinessCreation = (month: number, year: number): boolean => {
    if (!businessCreationDate || !businessCreationYear || businessCreationMonth === null) {
      return false;
    }
    
    if (year < businessCreationYear) return true;
    if (year === businessCreationYear && month < businessCreationMonth) return true;
    return false;
  };

  /**
   * Check if year navigation should be disabled
   * @param targetYear - Year to navigate to
   * @param direction - Navigation direction
   */
  const isYearNavigationDisabled = (targetYear: number, direction: 'previous' | 'next'): boolean => {
    if (!businessCreationYear) return false;
    
    if (direction === 'previous') {
      return targetYear < businessCreationYear;
    }
    
    // For next year, we generally don't restrict future navigation
    // but you can add future date restrictions here if needed
    return false;
  };

  /**
   * Check if month navigation (prev/next month arrows) should be disabled
   * @param currentMonth - Current month (0-based)
   * @param currentYear - Current year
   * @param direction - Navigation direction
   */
  const isMonthNavigationDisabled = (
    currentMonth: number, 
    currentYear: number, 
    direction: 'previous' | 'next'
  ): boolean => {
    if (!businessCreationDate || !businessCreationYear || businessCreationMonth === null) {
      return false;
    }

    if (direction === 'previous') {
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      return isBeforeBusinessCreation(prevMonth, prevYear);
    }
    
    if (direction === 'next') {
      // Generally don't restrict future navigation, but you can add restrictions here
      return false;
    }

    return false;
  };

  return {
    isBeforeBusinessCreation,
    isYearNavigationDisabled,
    isMonthNavigationDisabled,
    businessCreationYear,
    businessCreationMonth,
    businessCreationDate,
  };
}

/**
 * Utility function for components that need just the basic validation
 * without the full hook interface
 */
export function createMonthValidator(businessData: BusinessCreationInput | BusinessDetails) {
  const businessCreationDate = parseBusinessCreationDate(businessData);
  const businessCreationYear = businessCreationDate ? getYear(businessCreationDate) : null;
  const businessCreationMonth = businessCreationDate ? getMonth(businessCreationDate) : null;

  return (month: number, year: number): boolean => {
    if (!businessCreationDate || !businessCreationYear || businessCreationMonth === null) {
      return false;
    }
    
    if (year < businessCreationYear) return true;
    if (year === businessCreationYear && month < businessCreationMonth) return true;
    return false;
  };
}

/**
 * Hook version for React components
 * Provides the same functionality as useMonthAvailability but with React hooks patterns
 */
export function useBusinessCreationValidation(
  businessData: BusinessCreationInput | BusinessDetails
): MonthAvailabilityResult {
  return useMonthAvailability(businessData);
}

// Re-export types for convenience
export type { BusinessCreationInput, BusinessDetails };